// app/javascript/landing/pages/ContactPage.tsx
import React, { useState } from "react";
import { motion, Variants } from "framer-motion";
import { SmartImage } from "../components/SmartImage";

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
  },
};

type FormState = {
  name: string;
  email: string;
  message: string;
};

const API_BASE_URL =
  window.location.hostname === "localhost" ||
  window.location.hostname.endsWith("lvh.me") ||
  window.location.hostname.endsWith("rdx.local")
    ? "http://api.lvh.me:3000" // ajusta si tu API corre en otro host/puerto
    : "https://api.metaversoconstruccion.com";

export function ContactPage() {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);
    setError(null);

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Por favor completa todos los campos.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/v1/contact_requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          contact_request: {
            name: form.name,
            email: form.email,
            message: form.message,
            context: "landing_contact_page",
          },
        }),
      });

      if (!res.ok) {
        throw new Error(`Status ${res.status}`);
      }

      setFeedback("Gracias por escribirnos. Te contactaremos pronto.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      setError("Hubo un problema al enviar el formulario. Intenta nuevamente.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      className="
        py-26 relative min-h-dvh w-full 
        flex items-center justify-end
        bg-black
        px-6 md:px-12
      "
    >
      <SmartImage
        src="/landing/contact/bg.png"
        fill
        wrapperClassName="absolute! inset-0"
      />

      <motion.div
        className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-8"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <h2 className="text-center text-2xl font-bold mb-6 text-purple-3">
          Contáctanos
        </h2>

        <form className="flex flex-col gap-3 text-sm" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1">
            <span className="text-purple-3">Nombre:</span>
            <input
              name="name"
              className="rounded-xl border border-black/20 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-purple-3"
              placeholder="Tu nombre"
              value={form.name}
              onChange={handleChange}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-purple-3">Correo:</span>
            <input
              type="email"
              name="email"
              className="rounded-xl border border-black/20 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-purple-3"
              placeholder="tucorreo@ejemplo.com"
              value={form.email}
              onChange={handleChange}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-purple-3">¿En qué te podemos ayudar?</span>
            <textarea
              name="message"
              className="rounded-xl border border-black/20 px-3 py-2 h-28 resize-none focus:outline-none focus:ring-1 focus:ring-purple-3"
              placeholder="Cuéntanos tu idea…"
              value={form.message}
              onChange={handleChange}
            />
          </label>

          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}

          {feedback && (
            <p className="text-xs text-green-700 mt-1">{feedback}</p>
          )}

          <button
            type="submit"
            className="btn-landing mt-5 disabled:opacity-60"
            disabled={submitting}
          >
            {submitting ? "Enviando..." : "Enviar"}
          </button>
        </form>
      </motion.div>
    </section>
  );
}
